import React, { useEffect, useMemo, useState } from "react";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import {
  Card,
  CardHeader,
  CardBody,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetLogs } from "api/logs/service";
import { useGetTenantUsers } from "api/users/service";
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2SearchFilterColProps,
  getTheme2SearchFilterFieldCss,
  getTheme2SearchFilterMultiSelectStyles,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { useNavigate } from "react-router-dom";
import { formatDateTimeForDisplay } from "../../utils/dateUtils";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

const LOG_TYPES = [
  "inventory",
  "deals",
  "settings",
  "users",
  "customers",
  "accounts",
  "channels",
  "reports",
];

const Logs = ({ activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1); // 1-based for API
  const [pageSize, setPageSize] = useState(20);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedLogType, setSelectedLogType] = useState(null);

  const selectedUserIds = useMemo(() => {
    return selectedUser?.map(user => user.value) || [];
  }, [selectedUser]);

  const { data: usersData } = useGetTenantUsers(1, 100);
  const { data, isLoading } = useGetLogs(page, pageSize, selectedUserIds, {
    type: selectedLogType || undefined,
  });

  const isTheme2 = shellThemeVariant === "2";
  const LogsTable = isTheme2 ? ReactTable2 : ReactTable;

  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const userMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const logTypeSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
    filterColClass: "search-logs-filter-col",
    md: "4",
  });
  const userColProps = isTheme2
    ? filterColProps
    : { md: "4", xs: "12" };
  const logTypeColProps = isTheme2
    ? filterColProps
    : { md: "4", xs: "12" };

  useEffect(() => {
    setPage(1);
  }, [selectedUser, selectedLogType]);

  const userOptions = useMemo(() => {
    return usersData?.results?.map(user => ({
      value: user.id,
      label: user.user_detail?.full_name
    })) || [];
  }, [usersData]);

  const logTypeOptions = useMemo(
    () =>
      LOG_TYPES.map((value) => ({
        value,
        label: t(`logs.types.${value}`),
      })),
    [t]
  );

  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderChangedData = (changedData) => {
    if (!changedData) return <span className="text-muted" style={{ fontSize: '11px' }}>{t('dashboard.noChangesRecorded')}</span>;

    let data = changedData;
    if (typeof changedData === "string") {
      try {
        data = JSON.parse(changedData);
      } catch (e) {
        return <span style={{ fontSize: '12px' }}>{changedData}</span>;
      }
    }

    if (typeof data === "object" && !Array.isArray(data)) {
      return (
        <div className="pl-3 py-3" style={{ background: 'rgba(0,0,0,0.01)', borderLeft: '3px solid #1d8cf8' }}>
          {Object.entries(data).map(([key, value], idx) => (
            <div key={idx} className="mb-0" style={{ fontSize: '12px' }}>
              <strong style={{ textTransform: 'capitalize', color: '#888' }}>{key.replace(/_/g, ' ')}:</strong> <span className="ml-1">{String(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    const nodes = Array.isArray(data) ? data : [];
    if (!nodes.length) return <span className="text-muted" style={{ fontSize: '11px' }}>-</span>;

    return (
      <div className="pl-3 py-3" style={{ background: 'rgba(0,0,0,0.01)', borderLeft: '3px solid #1d8cf8' }}>
        {nodes.map((node, idx) => {
          if (typeof node === "object" && node.children) {
            const text = (node?.children || [])
              .map((c) => (c && typeof c.text === "string" ? c.text : ""))
              .join("");
            return <p key={idx} className="mb-1" style={{ fontSize: '12px' }}>{text || "-"}</p>;
          }
          if (typeof node === "object") {
            return (
              <div key={idx} className="mb-2 border-bottom pb-2">
                {Object.entries(node).map(([k, v], i) => (
                  <div key={i} className="mb-1" style={{ fontSize: '12px' }}>
                    <strong style={{ textTransform: 'capitalize', color: '#888' }}>{k.replace(/_/g, ' ')}:</strong> <span className="ml-1">{String(v)}</span>
                  </div>
                ))}
              </div>
            );
          }
          return <p key={idx} className="mb-1" style={{ fontSize: '12px' }}>{String(node)}</p>;
        })}
      </div>
    );
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data?.results || data?.data || data || [];
    return (Array.isArray(list) ? list : []).map((log, idx) => ({
      id: log?.id || idx + 1,
      userName: log?.user_name || "-",
      userEmail: log?.user_email || "-",
      userId: log?.user_id || "-",
      action: log?.action || "-",
      link: log?.link || null,
      createdAt: log?.created_at || log?.createdAt || "-",
      changedData: log?.changed_data,
    }));
  }, [data]);

  const secondaryCellClass = "search-logs-list-secondary-text";

  const columns = [
    {
      Header: t('logs.userName'),
      accessor: "userName",
      Cell: ({ row }) => {
        const { original } = row;
        return (
          <span className="log-user-tag" onClick={() => navigate(`/users/${original.userId}`)}>
            {original.userName}
          </span>
        );
      },
    },
    {
      Header: t('logs.email'),
      accessor: "userEmail",
      Cell: ({ value }) => <span className={secondaryCellClass}>{value}</span>,
    },
    {
      Header: t('logs.action'),
      accessor: "action",
      Cell: ({ row }) => {
        const { original } = row;
        const clickable = !!original.link;
        return (
          <span
            className={clickable ? "log-action-link" : "font-weight-600"}
            onClick={() => {
              if (original.link) navigate(original.link);
            }}
          >
            {original.action}
          </span>
        );
      },
    },
    {
      Header: t('logs.date'),
      accessor: "createdAt",
      Cell: ({ value }) => (
        <span className={secondaryCellClass}>{formatDateTimeForDisplay(value)}</span>
      ),
    },
    {
      Header: t('dashboard.details'),
      accessor: "id",
      Cell: ({ row }) => {
        const { original } = row;
        const isExpanded = !!expandedRows[original.id];
        const hasChangedData = original.changedData && (
          (typeof original.changedData === 'object' && Object.keys(original.changedData).length > 0) ||
          (typeof original.changedData === 'string' && original.changedData.length > 0)
        );

        if (!hasChangedData) return <span className="text-muted small">-</span>;

        return (
          <span
            className="btn-see-detail"
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => toggleExpand(original.id)}
          >
            {isExpanded ? t('dashboard.hide') : t('dashboard.details')}
            <i className={`tim-icons ${isExpanded ? 'icon-minimal-up' : 'icon-minimal-down'} ml-1`} style={{ fontSize: '0.6rem' }} />
          </span>
        );
      },
      sortable: false,
      filterable: false,
      width: 120,
    },
  ];

  return (
    <div
      className={classNames("content", "search-logs", {
        "search-logs--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-logs-inset": isTheme2,
      })}
    >
      <style>
        {`
          .search-logs-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .search-logs-card-header h2,
          .search-logs-card-header h4 {
            font-size: 16px !important;
            margin-top: 10px;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .search-logs .card .card-body {
            padding: 15px;
            padding-top: 10px;
            padding-bottom: 0 !important;
          }
          body:not(.white-content) .search-logs .search-logs-list-secondary-text {
            color: #ffffff !important;
          }
          .white-content .search-logs .search-logs-list-secondary-text {
            color: #6c757d !important;
          }
        `}
      </style>
      {isTheme2 && (
        <style>
          {`
          ${getTheme2SearchFilterFieldCss({
            pageScopeSelector: ".search-logs.search-logs--theme2",
            filtersSelector: ".search-logs-filters",
            rowClass: "search-logs-filters-row",
            colClass: "search-logs-filter-col",
          })}
          .search-logs.search-logs--theme2 .search-logs-filters-row {
            column-gap: 4px;
          }
          .search-logs.search-logs--theme2 .search-logs-filter-col {
            padding-left: 4px;
            padding-right: 4px;
          }
          .search-logs.search-logs--theme2 .search-logs-filter-col:first-child {
            padding-left: 0;
          }
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: left !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: left;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: left;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
        </style>
      )}
      <style>
        {`
          .card-modern {
            border: 1px solid rgba(255, 255, 255, 0.05) !important;
            transition: none;
            overflow: hidden;
            background: rgba(30, 30, 47, 0.95);
          }
          
          .white-content .card-modern {
            background: #ffffff !important;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02) !important;
            border: 1px solid rgba(0, 0, 0, 0.03) !important;
          }
          
          .btn-see-detail {
            color: #1d8cf8;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            cursor: pointer;
            padding: 5px 0px;
          }
          
          .btn-see-detail:hover {
            text-decoration: underline !important;
          }
          
          .table-modern thead th {
            text-transform: uppercase !important;
            font-size: 0.7rem !important;
            font-weight: 700 !important;
            padding: 10px 10px !important;
            letter-spacing: 0.5px;
            border-top: none !important;
            border-bottom: 1px solid rgba(0,0,0,0.05) !important;
          }
          
          .table-modern tbody td {
            font-size: 0.85rem !important;
            padding: 8px 10px !important;
            vertical-align: middle !important;
          }
          
          .log-user-tag {
            background: rgba(29, 140, 248, 0.08);
            color: #1d8cf8;
            padding: 4px 12px;
            font-weight: 600;
            font-size: 0.8rem;
            cursor: pointer;
            display: inline-block;
            transition: all 0.2s;
            text-decoration: underline;
          }

          .log-user-tag:hover {
            background: rgba(29, 140, 248, 0.15);
            transform: scale(1.05);
          }
          
          .log-action-link {
            color: #1d8cf8;
            font-weight: 500;
            cursor: pointer;
            text-decoration: underline;
          }

          .log-action-link:hover {
            color: #0d6efd;
            text-decoration: underline;
          }
        `}
      </style>
      <Row>
        <Col md="12">
          <Card className="card-modern">
            <CardHeader className="search-logs-card-header">
              <h2 className="mb-0">{t('logs.title')}</h2>
            </CardHeader>
            <CardBody>
              <div className={isTheme2 ? "search-logs-filters" : undefined}>
                <Form>
                  <Row className={isTheme2 ? "search-logs-filters-row" : "mb-3"}>
                    <Col {...userColProps}>
                      <FormGroup className="mb-0">
                        <Label className="small text-muted  font-weight-bold">{t('reports.filterByUser') || 'Filter by User:'}</Label>
                        <Select
                          activeColor={activeColor}
                          className="react-select w-100"
                          styles={userMultiSelectStyles}
                          placeholder={t('reports.filterByUser')}
                          name="userFilter"
                          isSearchable
                          isMulti
                          closeMenuOnSelect={false}
                          value={selectedUser}
                          onChange={(option) => setSelectedUser(option)}
                          options={userOptions}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                    <Col {...logTypeColProps}>
                      <FormGroup className="mb-0">
                        <Label className="small text-muted font-weight-bold">{t("logs.filterByType")}</Label>
                        <Select
                          activeColor={activeColor}
                          className="react-select w-100"
                          styles={logTypeSelectStyles}
                          placeholder={t("logs.filterByType")}
                          name="logTypeFilter"
                          isSearchable={false}
                          closeMenuOnSelect
                          value={
                            selectedLogType
                              ? logTypeOptions.find((o) => o.value === selectedLogType) || null
                              : null
                          }
                          onChange={(option) => setSelectedLogType(option?.value || null)}
                          options={logTypeOptions}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </div>
              {isTheme2 ? (
                <div
                  className="inventory-table-wrap"
                  style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
                >
                  <LogsTable
                    activeColor={activeColor}
                    data={rows}
                    columns={columns}
                    className="table-modern -striped -highlight sticky-stock-table"
                    manualPagination={true}
                    pageCount={data?.pagination?.pageCount || 1}
                    pageIndex={page - 1}
                    totalItems={data?.pagination?.total}
                    pageSize={pageSize}
                    noDataText={t("logs.noRecordsFound")}
                    loading={isLoading}
                    loadingText={t("logs.loading")}
                    onPageChange={(pageIndex) => {
                      setPage(pageIndex + 1);
                    }}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                    }}
                    renderExpandedRow={(original) => (
                      <div className="px-0 py-0">
                        {renderChangedData(original.changedData)}
                      </div>
                    )}
                    isRowExpanded={(original) => !!expandedRows[original.id]}
                  />
                </div>
              ) : (
                <LogsTable
                    activeColor={activeColor}
                    data={rows}
                    columns={columns}
                    className="table-modern -striped -highlight"
                    manualPagination={true}
                    pageCount={data?.pagination?.pageCount || 1}
                    pageIndex={page - 1}
                    pageSize={pageSize}
                    totalItems={data?.pagination?.total}
                    noDataText={t("logs.noRecordsFound")}
                    loading={isLoading}
                    loadingText={t("logs.loading")}
                    onPageChange={(pageIndex) => {
                      setPage(pageIndex + 1);
                    }}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                    }}
                    renderExpandedRow={(original) => (
                      <div className="px-0 py-0" >
                        {renderChangedData(original.changedData)}
                      </div>
                    )}
                    isRowExpanded={(original) => !!expandedRows[original.id]}
                  />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Logs;


